/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { NetworkStatus, useLazyQuery, useQuery } from '@apollo/client';
import { Box, DialogContent, Modal, Tooltip } from '@mui/material';
import Alert from '@mui/material/Alert';
import { makeStyles, withStyles } from '@mui/styles';

import * as Q from '../../../../../../constants/graphqlQueries';
import colors from '../../../../../../constants/colors';

import CustomDropdown from '../../../../../components/Dropdown/CustomDropdown';
import CustomTextField from '../../../../../components/Textfield/CustomTextField';
import CustomButtons from '../../../../../components/Button/CustomButtons';
import ModalDetail from '../../../../../ModalDetail/ModalDetail';
import SwitchCustom from '../../../../../Switch/SwitchCustom';

import './DimensionRC.css';

const useStyles = makeStyles({
  alert: {
    fontSize: '1rem',
    color: colors.danger
  }
});

const noZoomChannelsFoundMessage = 'There are no Zoom Channels available, Group Chat option is not available';
const segmentId = 2;
const adherenceReminderData = {
  rangeData: [
    {interval: '5'},
    {interval: '10'},
    {interval: '15'},
    {interval: '20'},
    {interval: '30'},
  ]
}

const generalIndexField = { RuleName: 1, ScheduleSegment: 2, ReminderTime: 3, usersToBeNotified: 4 }

const buttonOption = {
  save: 'Save',
  next: 'Next'
}

const modalOptions = { title: 'Choose rule default status: ' }

const modalRestrictOption = {
  title: 'Restricted action',
  description: 'The rule you are trying to create already exists'
}

const optionsSwitch = {
  agent: {
    rightTitle: 'Agent'
  },
  GA: {
    rightTitle: 'G&A'
  },
  groupChat: {
    rightTitle: 'Group chat'
  }
}

const agentValidationError = 'Notifications will only reach employees via Zoom, an existing TTEC email address is required.';

const StyledTooltip = withStyles({
  arrow: {
    color: '#F9F9F9'
  },
  tooltip: {
    backgroundColor: '#F9F9F9',
    color: colors.black,
    boxShadow: '0 0 10px rgba(0, 0, 0, 0.35)',
    padding: '1.3rem'
  }
})(Tooltip);

const DimensionAdherence = ({ handleDimensionSubmit, handleBack, codeTypes, dimensionPreview, ruleToEdit }) => {
  const classes = useStyles();
  const [buttonName, setButtonName] = useState(buttonOption.save);
  const [inputFieldDisabled, setInputFieldDisabled] = useState(1);
  const [ruleName, setRuleName] = useState('');
  const [segmentSelected, setSegmentSelected] = useState();
  const [reminderSelected, setReminderSelected] = useState();
  const [agentStatus, setAgentStatus] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [GAStatus, setGAStatus] = useState(false);
  const [selectedGA, setSelectedGA] = useState('');
  const [groupChatStatus, setGroupChatStatus] = useState(false);
  const [channels, setChannels] = useState({});
  const [selectedChannel, setSelectedChannel] = useState();

  const [scheduleSegment, setScheduleSegment] = useState({});
  const [reminderData, setReminderData] = useState({});  
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [openRuleDuplicatedModal, setOpenRuleDuplicatedModal] = useState(false);
  const [ruleNameDuplicated, setRuleNameDuplicated] = useState([]);

  const [loadingScreen, setLoadingScreen] = useState(false);

  const [openModal, setOpenModal] = useState(false);

  const [getIsRuleDuplicated, { data: dataRuleIsDuplicated, networkStatus: dataRuleIsDuplicatedNetworkStatus }] = useLazyQuery(Q.VALIDATE_ADHERENCE_RULE_DUPLICATED, { notifyOnNetworkStatusChange: true });
  const { data: scheduleTypeData } = useQuery(Q.CODE__MAPPING__SCHEDULE);
  const { data: zoomChannelsData } = useQuery(Q.GET_ZOOM_CHANNELS);

  const handleCloseRuleDuplicatedModal = () => {
    setOpenRuleDuplicatedModal(false);
  }

  // Determine if input is disabled
  const isInputFieldDisabled = (inputIndex) => {
    let result = false;
    if (inputIndex > inputFieldDisabled && inputFieldDisabled) {
      result = true;
    }

    return result;
  }

  const handleRuleName = (value) => {
    setRuleName(value);
  }

  const handleSegment = (item) => {
    setSegmentSelected(item);
  }

  const handleReminder = (item) => {
    setReminderSelected(item);
  }

  const handleToggleAgent = (checked) => {
    setAgentStatus(checked);
    if (!checked) {
      setSelectedAgent('');
    };
  }

  const handleNotificationAgent = (value) => setSelectedAgent(value);

  const handleToggleGA = (checked) => {
    setGAStatus(checked);
    if (!checked) {
      setSelectedGA('');
    };
  }

  const handleNotificationGA = (value) => setSelectedGA(value);

  const handleGroupChat = (checked) => {
    setGroupChatStatus(checked);
    if (!checked) {
      setSelectedChannel('');
    }
  }

  const handleChannel = (item) => {
    setSelectedChannel(item)
  }

  const handleCancel = (isRestart) => {
    setRuleName('');
    setSegmentSelected(null);
    setReminderSelected(null);
    setAgentStatus(false);
    setSelectedAgent('');
    setGAStatus(false);
    setSelectedGA('');
    setSelectedChannel();
    setGroupChatStatus(false);

    if(isRestart) handleBack('Cancel');
  }

  const submitDimensionData = (status=null) => {
    const dimensionPerformanceData = [{
      ruleName,
      segmentId: segmentSelected.CodeMappingId,
      segment: segmentSelected.Label,
      reminder: reminderSelected.interval,
      agent_status: !agentStatus ? 0 : 1,
      agent_Notification: selectedAgent,
      GA_status: !GAStatus ? 0 : 1,
      GA_Notification: selectedGA,
      isActive: status,
      Channel: selectedChannel || null
    }];
    handleDimensionSubmit(dimensionPerformanceData);
  }

  const validateRuleName = () => {
    if (codeTypes) submitDimensionData();
    else {
      let validationParams = { ScheduleSegmentCode: segmentSelected.Label, RuleName: ruleName }

      if (ruleToEdit) {
        validationParams = { ...validationParams, RuleId: ruleToEdit }
      }

      getIsRuleDuplicated({
            variables: {
              ...validationParams
            }
          });
      setLoadingSubmit(true);
    }
  }

  const sendingData = () => {
    if (!ruleToEdit) setOpenModal(true);
    else submitDimensionData();
  }

  const handleClickSave = () => {
    validateRuleName();
  }

  useEffect(() => {
    if (ruleNameDuplicated.length) {
      setOpenRuleDuplicatedModal(true);
    }
  }, [ruleNameDuplicated]);

  useEffect(() => {
    if (scheduleTypeData) {
      const scheduleSegmentData = {
        options: scheduleTypeData.codeMappings.filter(types => types.ScheduleTypeId === segmentId),
        getOptionLabel: (option) => option.Label
      };
      setScheduleSegment(scheduleSegmentData);
    }
  }, [scheduleTypeData]);

  useEffect(() => {
    if (adherenceReminderData) {
      const reminderProps = {
        options: adherenceReminderData.rangeData,
        getOptionLabel: (option) => option.interval
      };
      setReminderData(reminderProps);
    }
  }, [adherenceReminderData]);

  useEffect(() => {
    if (dimensionPreview.length && Object.keys(scheduleSegment).length && Object.keys(channels).length) {
      setRuleName(dimensionPreview[0].ruleName);
      setSegmentSelected({
        Label: dimensionPreview[0].segment,
        CodeMappingId: dimensionPreview[0].segmentId
      });
      setReminderSelected({ interval: dimensionPreview[0].reminder });

      if (dimensionPreview[0].agent_status === 1) {
        setAgentStatus(true);
        setSelectedAgent(dimensionPreview[0].agent_Notification);
      }

      if (dimensionPreview[0].GA_status === 1) {
        setGAStatus(true);
        setSelectedGA(dimensionPreview[0].GA_Notification);
      }

      if (dimensionPreview[0].Channel) {
        setGroupChatStatus(true);
        setSelectedChannel(dimensionPreview[0].Channel);
      }
      setLoadingScreen(false);
    }
  }, [scheduleSegment, channels]);

  useEffect(() => {
    if (codeTypes) {
      setInputFieldDisabled(generalIndexField.ScheduleSegment);
      setButtonName(buttonOption.next);
      handleCancel();
    } else setButtonName(buttonOption.save);
  }, [codeTypes]);

  useEffect(() => {
    if (!ruleName && !codeTypes) setInputFieldDisabled(generalIndexField.RuleName);
    else if (!segmentSelected) setInputFieldDisabled(generalIndexField.ScheduleSegment);
    else if (!reminderSelected) setInputFieldDisabled(generalIndexField.ReminderTime);
    else if (agentStatus && !selectedAgent) setInputFieldDisabled(generalIndexField.usersToBeNotified);
    else if (GAStatus && !selectedGA) setInputFieldDisabled(generalIndexField.usersToBeNotified);
    else if (groupChatStatus && !selectedChannel) setInputFieldDisabled(generalIndexField.usersToBeNotified);
    else if (!agentStatus && !GAStatus && !groupChatStatus) setInputFieldDisabled(generalIndexField.usersToBeNotified);
    else setInputFieldDisabled(null);
  }, [ruleName, segmentSelected, reminderSelected, agentStatus, selectedAgent, GAStatus, selectedGA, groupChatStatus, selectedChannel]);

  useEffect(() => {
    if (dataRuleIsDuplicated && dataRuleIsDuplicatedNetworkStatus === NetworkStatus.ready) {
      if (dataRuleIsDuplicated.duplicatedAdherenceRuleValidation) {
        if (!(dataRuleIsDuplicated.duplicatedAdherenceRuleValidation.result)) {
          sendingData();
        }
        else if (ruleNameDuplicated !== dataRuleIsDuplicated.duplicatedAdherenceRuleValidation.details) {
          setRuleNameDuplicated(dataRuleIsDuplicated.duplicatedAdherenceRuleValidation.details);
        }
        else if (ruleNameDuplicated.length) {
          setOpenRuleDuplicatedModal(true);
        }
      }

      setLoadingSubmit(false);
    }
  }, [dataRuleIsDuplicated, dataRuleIsDuplicatedNetworkStatus]);

  useEffect(() => {
    if (ruleToEdit) setLoadingScreen(true);
  }, [ruleToEdit]);

  useEffect(() => {
    if (zoomChannelsData) {
      let channelProps = {
        options: [{ Name: 'No channels available' }],
        getOptionLabel: (option) => option.Name
      }
      if (zoomChannelsData.getZoomChannels.length){
        channelProps = {
          options: zoomChannelsData.getZoomChannels,
          getOptionLabel: (option) => option.Name
        }
      }
      setChannels(channelProps);
    }
  }, [zoomChannelsData]);

  return (
    <>
    <div className='dimension__wrapper'>
      <div className='dimension__wrapper__performance'>
        {!codeTypes && (
          <CustomTextField
            title='Rule Name'
            value={ruleName}
            handleChange={handleRuleName}
            placeholder='Select option(s)'
            mandatory
            loading={loadingScreen}
            maxLength={25}
          />
        )}
        <CustomDropdown
          title='Schedule Segment'
          value={segmentSelected}
          handleChange={handleSegment}
          defaultProps={scheduleSegment}
          disabled={isInputFieldDisabled(generalIndexField.ScheduleSegment)}
          mandatory
          loading={loadingScreen}
        />
        <CustomDropdown
          title='Reminder Time (in minutes)'
          value={reminderSelected}
          handleChange={handleReminder}
          defaultProps={reminderData}
          disabled={isInputFieldDisabled(generalIndexField.ReminderTime)}
          mandatory
          loading={loadingScreen}
        />
      </div>
      <div className='dimension__wrapper__performance'>
        <p className='tagging__wrapper__autocomplete__title'>Users to be notified</p>
          <SwitchCustom
            switchStatus={agentStatus}
            options={optionsSwitch.agent}
            handleSwitch={handleToggleAgent}
            disabledStatus={isInputFieldDisabled(generalIndexField.usersToBeNotified)}
          />
          <Box style={{ display: !agentStatus ? 'none' : '' }}>
            <StyledTooltip title={agentValidationError}
              placement='top'
              enterDelay={400}
              leaveDelay={200}
              arrow
            >
              <div>
                <CustomTextField
                  value={selectedAgent}
                  handleChange={handleNotificationAgent}
                  placeholder='Input text message'
                  multiline
                  rows={2}
                  maxLength={100}
                />
              </div>
            </StyledTooltip>
          </Box>
          <SwitchCustom
            switchStatus={GAStatus}
            options={optionsSwitch.GA}
            handleSwitch={handleToggleGA}
            disabledStatus={isInputFieldDisabled(generalIndexField.usersToBeNotified)}
          />
          <Box style={{ display: !GAStatus ? 'none' : '' }}>
            <CustomTextField
              value={selectedGA}
              handleChange={handleNotificationGA}
              placeholder='Input text message'
              multiline
              rows={2}
              maxLength={100}
            />
          </Box>
          <SwitchCustom
            switchStatus={groupChatStatus}
            options={optionsSwitch.groupChat}
            handleSwitch={handleGroupChat}
            disabledStatus={isInputFieldDisabled(generalIndexField.usersToBeNotified) || !(zoomChannelsData && zoomChannelsData?.getZoomChannels.length)}
          />
          {
            !isInputFieldDisabled(generalIndexField.usersToBeNotified) && !(zoomChannelsData?.getZoomChannels.length) &&
            <Alert
              className={classes.alert}
              severity='warning'>
                {noZoomChannelsFoundMessage}
            </Alert>
          }
          <Box style={{ display: !groupChatStatus ? 'none' : '' }}>
            <CustomDropdown
              title='Channels'
              value={selectedChannel}
              handleChange={handleChannel}
              defaultProps={channels}
              disabled={!groupChatStatus}
            />
          </Box>
      </div>
    </div>
    <div className='create__wrapper_btn_performance'>
      <CustomButtons
        handleClick={() => handleCancel(true)}
        name='Cancel'
        loading={loadingScreen}
      />
      {codeTypes && 
        <CustomButtons
          handleClick={handleBack}
          name='Back'
        />
      }
      <CustomButtons
        handleClick={handleClickSave}
        name={buttonName}
        disabled={inputFieldDisabled !== null}
        tooltipFree
        loadingButton={loadingSubmit}
        loading={loadingScreen}
      />
    </div>
    <Modal
      aria-labelledby='restricted-modal-title'
      aria-describedby='restricted-modal-description'
      open={openRuleDuplicatedModal}
      onClose={handleCloseRuleDuplicatedModal}
    >
      <DialogContent>
        <ModalDetail modalOptions={modalRestrictOption}>
          <div className='restricted-modal-container'>
            <ul className='restricted-modal-list'>
              <li key={ruleNameDuplicated}>
                  {ruleNameDuplicated}
              </li>
            </ul>
          </div>
          <button className='simple-modal-btn' type='button' onClick={handleCloseRuleDuplicatedModal}>Ok</button>
        </ModalDetail>
      </DialogContent>
    </Modal>
    <Modal
        aria-labelledby='transition-modal-title'
        aria-describedby='transition-modal-description'
        open={openModal}
      >
        <DialogContent>
          <ModalDetail
            modalOptions={modalOptions}>
            <button className='simple-modal-btn' type='button' onClick={() => submitDimensionData(true)}>Active</button>
            <button className='simple-modal-btn' type='button' onClick={() => submitDimensionData(false)}>Paused</button>
          </ModalDetail>
        </DialogContent>
      </Modal>
    </>
  )
};

DimensionAdherence.propTypes = {
  handleDimensionSubmit: PropTypes.func.isRequired,
  handleBack: PropTypes.func.isRequired,
  codeTypes: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  dimensionPreview: PropTypes.array.isRequired,
  ruleToEdit: PropTypes.number
};

DimensionAdherence.defaultProps = {
  ruleToEdit: null
}


export default DimensionAdherence
